"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IMAGES } from "@/constants/images";
const steps = [
  {
    number: "1",
    titleKey: "howItWorks.step1Title",
    descKey: "howItWorks.step1Desc",
  },
  {
    number: "2",
    titleKey: "howItWorks.step2Title",
    descKey: "howItWorks.step2Desc",
  },
  {
    number: "3",
    titleKey: "howItWorks.step3Title",
    descKey: "howItWorks.step3Desc",
  },
];

const trustItems = [
  "howItWorks.trust1",
  "howItWorks.trust2",
  "howItWorks.trust3",
];

const HowItWorks = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        py: { xs: 2, sm: 5, md: 4 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* ── EYEBROW PILL ── */}
          <Box
            sx={{
              backgroundColor: "#EDE9FE",
              borderRadius: "50px",
              px: 2,
              py: 1,
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 600,
                fontSize: { xs: "0.7rem", sm: "1rem" },
                color: "#7C3AED",
              }}
            >
              {t("howItWorks.eyebrow")}
            </Typography>
          </Box>

          {/* ── HEADING ── */}
          <Typography
            component="h2"
            sx={{
              fontFamily: "var(--font-plusjakarta), sans-serif",
              fontWeight: 800,
              fontSize: { xs: "1.5rem", sm: "2rem", md: "3.2rem" },
              color: "#0F0A1E",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              mb: { xs: 4, sm: 5, md: 6 },
              px: { xs: 1, sm: 2, md: 0 },
            }}
          >
            {t("howItWorks.heading1")}{" "}
            <Box component="br" sx={{ display: { xs: "none", md: "block" } }} />
            {t("howItWorks.heading2")}
          </Typography>

          {/* ── STEP CARDS ── */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, sm: 2.5, md: 3 },
              width: "100%",
              mb: { xs: 4, sm: 5, md: 6 },
            }}
          >
            {steps.map((step) => (
              <Box
                key={step.number}
                sx={{
                  flex: 1,
                  backgroundColor: "#F9F9F9",
                  border: "1px solid #EDE9FE",
                  borderRadius: "16px",
                  textAlign: isArabic ? "right" : "left",
                  p: { xs: 2.5, sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 700,
                    fontSize: { xs: "1.4rem", sm: "2.2rem" },
                    color: "#7C3AED",
                    mb: 1,
                  }}
                >
                  {step.number}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.95rem", sm: "1.4rem" },
                    color: "#0F0A1E",
                    mb: 1,
                  }}
                >
                  {t(step.titleKey)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    color: "#4B5563",
                    lineHeight: 1.6,
                  }}
                >
                  {t(step.descKey)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── PILL CTA BUTTON ── */}
          <Button
            className="btn-how-it-works"
            id="btn-three"
            // onClick={() => {
            //   const el = document.getElementById("book-demo-section");
            //   if (el) el.scrollIntoView({ behavior: "smooth" });
            // }}
            onClick={() => {
              window.dispatchEvent(new Event("open-book-demo-modal"));
            }}
            endIcon={
              isArabic ? (
                <ArrowBackIcon sx={{ mr: "60%", mt: "20%" }} />
              ) : (
                <ArrowForwardIcon />
              )
            }
            sx={{
              background: "#7C3AED",
              color: "#fff",
              fontFamily: "var(--font-outfit), sans-serif",
              fontWeight: 600,
              boxShadow: "0 4px 20px rgba(124,58,237,0.4)",

              fontSize: { xs: "0.78rem", sm: "0.82rem", md: "1rem" },
              px: { xs: 3, sm: 3.5, md: 4 },
              py: { xs: 1.3, sm: 1.5, md: 1.8 },
              borderRadius: "50px",
              textTransform: "none",
              transition: "all 0.3s ease",
              mb: { xs: 3, sm: 3.5, md: 4 },
              "&:hover": {
                background: "#7C3AED",
                transform: "scale(1.05)",
              },
              "&:active": { transform: "translateY(0)" },
            }}
          >
            {t("howItWorks.cta")}
          </Button>

          {/* ── TRUST LAYOUT LIST ── */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              columnGap: { xs: 1.5, sm: 2, md: 2.5 },
              rowGap: { xs: 1.2, sm: 1.5 },
              width: "100%",
              mb: { xs: "5%", md: 0 },
            }}
          >
            {trustItems.map((key) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "50px",
                  px: { xs: 1.5, sm: 2, md: 2 },
                  py: { xs: 0.6, sm: 0.75, md: 1 },
                }}
              >
                <Box
                  component="img"
                  src={IMAGES.SLIDER_TICK}
                  alt="check"
                  sx={{
                    width: { xs: "16px", sm: "18px" },
                    height: { xs: "16px", sm: "18px" },
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit), sans-serif",
                    fontSize: { xs: "0.72rem", sm: "0.78rem", md: "0.95rem" },
                    color: "#4B5563",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                  }}
                >
                  {t(key)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
